import type { ErrorLike } from '@apollo/client';
import { faArrowDown } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CircularProgress } from '@mui/material';
import * as React from 'react';
import styled from 'styled-components';
import useTracking, { TrackingEventData } from '../../hooks/useTracking';
import { EventNames } from '../../utils/trackingEvents';
import Button, { ButtonSize, ButtonVariant } from './Button';
import { LinkButton } from './Button/LinkButton';
import Text, { TextKind } from './Text';

const LoadMoreContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 5px;
`;

export enum LoadMoreSize {
  Large = 'large',
  Small = 'small',
}

export const getLoadMoreSizes = (
  size: LoadMoreSize,
): { text: TextKind; buttonSize: ButtonSize } => {
  if (size === 'small') {
    return {
      text: TextKind.ButtonM,
      buttonSize: ButtonSize.Small,
    };
  }
  return {
    text: TextKind.ButtonL,
    buttonSize: ButtonSize.Medium,
  };
};

const LoadMore: React.FC<{
  error?: ErrorLike;
  isFetchingMore?: boolean;
  loadMore: () => void;
  size?: LoadMoreSize;
  flipArrow?: boolean;
  trackingData?: Partial<TrackingEventData>;
}> = ({
  error,
  isFetchingMore,
  loadMore,
  size = LoadMoreSize.Large,
  flipArrow = false,
  trackingData,
}) => {
  const { text, buttonSize } = getLoadMoreSizes(size);
  const { track } = useTracking();

  const handleLoadMoreClick = () => {
    loadMore();
    if (trackingData) {
      track(EventNames.LoadMore, trackingData);
    }
  };

  const handleRetryClick = () => {
    loadMore();
    if (trackingData) {
      track(EventNames.LoadMoreRetry, trackingData);
    }
  };

  if (error) {
    return (
      <LoadMoreContainer>
        <Text kind={text}>
          Unable to reload.{' '}
          <LinkButton testId="LoadMore-retry" onClick={handleRetryClick}>
            Retry
          </LinkButton>
        </Text>
      </LoadMoreContainer>
    );
  }

  if (isFetchingMore) {
    return (
      <LoadMoreContainer>
        <Button
          testId="LoadMore-loading"
          variant={ButtonVariant.Outline}
          icon={<CircularProgress size={14} />}
          size={buttonSize}
        >
          Load More
        </Button>
      </LoadMoreContainer>
    );
  }

  return (
    <LoadMoreContainer>
      <Button
        variant={ButtonVariant.Outline}
        onClick={handleLoadMoreClick}
        testId="LoadMore-ready"
        icon={
          <FontAwesomeIcon
            icon={faArrowDown}
            rotation={flipArrow ? 180 : undefined}
          />
        }
        size={buttonSize}
      >
        Load More
      </Button>
    </LoadMoreContainer>
  );
};

export default LoadMore;
